import { getValidToken } from './token-service';
import { clearTokenCache } from "@/services/token-service";

// Client-side token retrieval (used in components)
export async function getClientSideAuth() {
  return await getValidToken();
}

/**
 * Add authorization header with Bearer token to request options
 * @param {Object} options - Fetch request options
 * @returns {Promise<Object>} - Updated options with auth header
 */
export async function withAuth(options = {}) {
  try {
    const auth = await getClientSideAuth();
    
    // Get preferred language from cache
    const preferredLanguage = localStorage.getItem('preferred_language') || 'en';
    
    return {
      ...options,
      headers: {
        ...options.headers,
        Authorization: `Bearer ${auth.accessToken}`,
        "X-User-Sub": auth.sub,
        "X-User-Email": auth.email,
        "Accept-Language": preferredLanguage
      }
    };
  } catch (error) {
    console.error('Error getting auth token:', error);
    throw error;
  }
}

export async function changeAccountInfo(data) {
  try {
    const authOptions = await withAuth({
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    
    const { baseApiUrl } = await import('./config');
    const response = await fetch(`${baseApiUrl}/auth/update-profile`, authOptions);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update profile');
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error in ChangeAccountInfo:', error);
    throw new Error(error instanceof Error ? error.message : 'Failed to update profile');
  }
}

export async function changePassword(data) {
  try {
    const authOptions = await withAuth({
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    
    const { baseApiUrl } = await import('./config');
    const response = await fetch(`${baseApiUrl}/auth/change-password`, authOptions);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to change password');
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error in ChangePassword:', error);
    throw new Error(error instanceof Error ? error.message : 'Failed to change password');
  }
}

export async function checkIfUserBlocked() {
  try {
    // Use withAuth to ensure proper authentication headers
    const authOptions = await withAuth({
      method: 'GET',
    });
    
    const { baseApiUrl } = await import('./config');
    const response = await fetch(`${baseApiUrl}/check-blocked`, authOptions);
    
    if (response.ok) {
      const data = await response.json();
      
      if (data.isBlocked) {
        // Clear token cache if user is blocked
        clearTokenCache();
        return true;
      }
      return false;
    } else if (response.status === 401) {
      // Authentication failed - clear cache and treat as blocked
      console.warn('Authentication failed when checking blocked status');
      clearTokenCache();
      return true;
    } else if (response.status === 404) {
      // Endpoint not implemented yet - fail open (not blocked)
      // Note: Browser will still show 404 in Network tab, this is expected
      return false;
    } else {
      console.error('Failed to check user blocked status:', response.status, response.statusText);
      // On other server errors, fail open to not block users unnecessarily
      return false;
    }
  } catch (error) {
    console.error('Error checking if user is blocked:', error);
    // On network/other errors, fail open to not block users unnecessarily
    return false;
  }
}